/**
 * Shiur times (docs/shiurim-plan.md). Pure logic: no Firebase, no clock.
 *
 * A shiur's sessions use the same day rules as opening hours (weekdays,
 * special days, one date, date range, winter/summer clock), with two
 * differences decided by Yoel (2026-10-22):
 * - Special days (Yom Tov, Chol HaMoed, Yom Kippur, Tisha B'Av, Purim, Erev
 *   Yom Tov, Erev Yom Kippur) have no shiur unless a session names that day.
 * - A shiur in a shul can start from the shul's minyan that day:
 *   "between Mincha and Maariv", before / after Shacharit, Mincha or Maariv.
 *   The minyan times come from resolveSchedule() (see shulMinyanTimes).
 */
import {
  DayType,
  HoursDayType,
  Shul,
  ShiurAnchorPick,
  ShiurAnchorPrayer,
  ShiurSession,
  ZmanimData,
} from '../types';
import { BETWEEN_MINCHA_MAARIV_DEFAULT_MINUTES } from '../config/categories';
import { getDayFacts } from './jewishCalendar';
import { addDays, dayOfWeekOf, hoursDayTypes, inSeason, regularOn, specialDayMatches, withHoursZmanim } from './openingHours';
import { resolveSchedule } from './scheduleResolver';
import { applyOffset, resolveTimeRule } from './timeUtils';
import { detectDayType } from './dayTypeDetector';

/** One shiur on one date. */
export interface ShiurOccurrence {
  sessionId: string;
  /** "HH:mm" */
  start: string;
  /** "HH:mm"; on the next day when endsNextDay. */
  end?: string;
  endsNextDay?: boolean;
  /** Set when the start comes from the shul's minyan, for "Between Mincha and Maariv". */
  anchor?: 'betweenMinchaMaariv' | 'before' | 'after';
  anchorPrayer?: ShiurAnchorPrayer;
  noteEn?: string;
  noteHe?: string;
}

/**
 * Why a date has no shiur although the shiur normally has one that weekday:
 * 'specialDay' = Yom Tov etc. with no time entered; 'cancelled' = a session
 * cancels this date; 'noMinyan' = tied to a minyan the shul doesn't have that day.
 */
export type NoShiurReason = 'specialDay' | 'cancelled' | 'noMinyan';

export interface ShiurDay {
  date: string;
  /** Sorted by start. */
  occurrences: ShiurOccurrence[];
  noShiurReason?: NoShiurReason;
  /** The day's types, for labels ("Pesach"). */
  dayTypes: HoursDayType[];
}

/** Times ("HH:mm", sorted) of the linked shul's minyanim of one prayer on one date. */
export type MinyanLookup = (prayer: ShiurAnchorPrayer) => string[];

/** True on the days that have no shiur unless the owner entered a time for them. */
export function isNoShiurDay(date: string): boolean {
  const f = getDayFacts(date);
  return f.yomTov || f.yomKippur || f.erevYomKippur || f.cholHaMoed || f.erevYomTov || f.tishaBav || f.purim;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** Which of the day's minyanim a session means, when the shul has several. */
export function pickMinyan(times: string[], pick: ShiurAnchorPick, sunset?: string): string | undefined {
  if (times.length === 0) return undefined;
  const sorted = [...times].sort();
  if (pick === 'earliest') return sorted[0];
  if (pick === 'latest' || !sunset) return sorted[sorted.length - 1];
  const target = toMinutes(sunset);
  // Closest to sunset; on a tie the earlier one (a Mincha before sunset).
  return sorted.reduce((best, t) =>
    Math.abs(toMinutes(t) - target) < Math.abs(toMinutes(best) - target) ? t : best
  );
}

function occurrenceOf(
  session: ShiurSession,
  zmanim: ZmanimData,
  minyanim: MinyanLookup | undefined
): ShiurOccurrence | undefined {
  const base = { sessionId: session.id, noteEn: session.noteEn, noteHe: session.noteHe };
  const rule = session.start;

  if (rule.type !== 'minyan') {
    const start = resolveTimeRule(rule, zmanim);
    const end = session.end ? resolveTimeRule(session.end, zmanim) : undefined;
    return { ...base, start, ...(end ? { end, endsNextDay: toMinutes(end) < toMinutes(start) } : {}) };
  }

  // Tied to the shul's minyan: nothing without a linked shul.
  if (!minyanim) return undefined;
  if (rule.anchor === 'betweenMinchaMaariv') {
    const mincha = pickMinyan(minyanim('mincha'), rule.pick, zmanim.SunsetDefault);
    if (!mincha) return undefined;
    const maariv = minyanim('maariv').find((t) => toMinutes(t) > toMinutes(mincha));
    if (!maariv) return undefined;
    const offset = Number.isFinite(rule.offsetMinutes) ? rule.offsetMinutes : BETWEEN_MINCHA_MAARIV_DEFAULT_MINUTES;
    const start = applyOffset(mincha, offset);
    // A start at or after Maariv leaves no time between them.
    if (toMinutes(start) >= toMinutes(maariv)) return undefined;
    return { ...base, start, end: maariv, anchor: 'betweenMinchaMaariv', anchorPrayer: 'mincha' };
  }

  const prayer = rule.prayer ?? 'maariv';
  const minyan = pickMinyan(minyanim(prayer), rule.pick, zmanim.SunsetDefault);
  if (!minyan) return undefined;
  const start = applyOffset(minyan, rule.anchor === 'before' ? -rule.offsetMinutes : rule.offsetMinutes);
  const end = session.end ? resolveTimeRule(session.end, zmanim) : undefined;
  return {
    ...base,
    start,
    ...(end ? { end, endsNextDay: toMinutes(end) < toMinutes(start) } : {}),
    anchor: rule.anchor,
    anchorPrayer: prayer,
  };
}

/** Which sessions apply on a date, and why none do. */
function sessionsForDate(
  sessions: ShiurSession[],
  date: string,
  dayTypes: HoursDayType[]
): { matched: ShiurSession[]; regular?: boolean; reason?: NoShiurReason } {
  const active = (sessions || []).filter((s) => inSeason(s, date));

  // 1. A session for this exact date wins (a cancellation too).
  const specific = active.filter((s) => s.specificDate === date);
  if (specific.length > 0) {
    return specific.every((s) => s.cancelled) ? { matched: [], reason: 'cancelled' } : { matched: specific.filter((s) => !s.cancelled) };
  }

  // 2. Sessions the owner entered for one of today's special days.
  const special = specialDayMatches(active, dayTypes);
  if (special.length > 0) {
    return special.every((s) => s.cancelled) ? { matched: [], reason: 'cancelled' } : { matched: special.filter((s) => !s.cancelled) };
  }

  // 3. Regular weekly sessions — none on the special days (Yoel, 2026-10-22).
  const regular = active.filter((s) => !s.cancelled && regularOn(s, dayOfWeekOf(date)));
  if (regular.length === 0) return { matched: [] };
  if (isNoShiurDay(date)) return { matched: [], reason: 'specialDay' };
  return { matched: regular, regular: true };
}

/**
 * One date's shiurim.
 * @param zmanim the date's zmanim; `nextDayZmanim` tells Erev Yom Kippur / Erev Tisha B'Av apart
 * @param minyanim the linked shul's minyan times on that date (shiurim in a shul only)
 */
export function resolveShiurDay(
  sessions: ShiurSession[],
  date: string,
  zmanim: ZmanimData,
  nextDayZmanim?: ZmanimData,
  minyanim?: MinyanLookup
): ShiurDay {
  // Purim isn't one of the hours day types; shiurim need it (no shiur by default, or the owner's Purim time).
  const dayTypes = hoursDayTypes(zmanim, nextDayZmanim);
  if (getDayFacts(date).purim && !dayTypes.includes('purim')) dayTypes.push('purim');
  const { matched, regular, reason } = sessionsForDate(sessions, date, dayTypes);
  const z = withHoursZmanim(zmanim);
  let occurrences = matched
    .map((s) => occurrenceOf(s, z, minyanim))
    .filter((o): o is ShiurOccurrence => !!o);

  // The night of Tisha B'Av begins on its erev: no regular evening shiur then.
  if (regular && dayTypes.includes('erevTishaBav') && zmanim.SunsetDefault) {
    occurrences = occurrences.filter((o) => toMinutes(o.start) < toMinutes(zmanim.SunsetDefault));
  }
  occurrences.sort((a, b) => toMinutes(a.start) - toMinutes(b.start));

  const noShiurReason = occurrences.length > 0 ? undefined : reason ?? (matched.length > 0 ? 'noMinyan' : undefined);
  return { date, occurrences, dayTypes, ...(noShiurReason ? { noShiurReason } : {}) };
}

/** Lookups the week view and "next shiur" need, so callers decide where data comes from. */
export interface ShiurContext {
  getZmanim: (date: string) => ZmanimData;
  /** For a shiur in a shul: the shul's minyan times on a date. */
  getMinyanim?: (date: string) => MinyanLookup;
}

/** `days` dates starting at `from` (the shiur page's week view). */
export function resolveShiurDays(sessions: ShiurSession[], from: string, days: number, ctx: ShiurContext): ShiurDay[] {
  return Array.from({ length: days }, (_, i) => {
    const date = addDays(from, i);
    return resolveShiurDay(sessions, date, ctx.getZmanim(date), ctx.getZmanim(addDays(date, 1)), ctx.getMinyanim?.(date));
  });
}

/** How far ahead to look for the next shiur. */
const NEXT_SHIUR_SEARCH_DAYS = 14;

/**
 * The next shiur that hasn't started yet (or started within `graceMinutes`,
 * so one that just began still shows as "now").
 */
export function nextShiur(
  sessions: ShiurSession[],
  now: { date: string; time: string },
  ctx: ShiurContext,
  graceMinutes = 30
): { date: string; occurrence: ShiurOccurrence } | undefined {
  for (const day of resolveShiurDays(sessions, now.date, NEXT_SHIUR_SEARCH_DAYS, ctx)) {
    const found = day.occurrences.find(
      (o) => day.date > now.date || toMinutes(o.start) + graceMinutes >= toMinutes(now.time)
    );
    if (found) return { date: day.date, occurrence: found };
  }
  return undefined;
}

/** The shul's minyan times on a date, through resolveSchedule() (unchanged minyan logic). */
export function shulMinyanTimes(shul: Shul, date: string, zmanim: ZmanimData): MinyanLookup {
  const dayType: DayType = detectDayType(zmanim);
  const cache = new Map<ShiurAnchorPrayer, string[]>();
  return (prayer) => {
    if (!cache.has(prayer)) {
      const times = resolveSchedule(shul, prayer, date, dayType, zmanim)
        .map((r) => r.displayTime)
        .filter((t) => /^\d{2}:\d{2}$/.test(t) && t !== '00:00');
      cache.set(prayer, Array.from(new Set(times)).sort());
    }
    return cache.get(prayer)!;
  };
}
